import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SharedHeader } from "@/components/SharedHeader";
import { Spade, Club, Diamond, Heart, Grid3X3, CircleDot } from "lucide-react";

const CARD_GAMES = [
  {
    id: "spades",
    name: "Spades",
    icon: Spade,
    rules: `
SPADES - Official Rules

OVERVIEW
Spades is a trick-taking card game for four players in two partnerships. The goal is to be the first team to reach 500 points.

SETUP
• Use a standard 52-card deck
• Players sit across from their partners
• Deal 13 cards to each player

BIDDING
• Starting with the dealer's left, each player bids the number of tricks they expect to win
• Bids range from 0 (nil) to 13
• Partners' bids are combined for a team bid
• A nil bid means you expect to win zero tricks

GAMEPLAY
• Player to dealer's left leads first trick
• Players must follow suit if possible
• Spades are always trump
• Highest card of led suit wins unless trumped
• Winner of trick leads next

SCORING
• Making your bid: 10 points per trick bid
• Overtricks (bags): 1 point each
• 10 bags = -100 point penalty
• Failed bid: Lose 10 points per trick bid
• Successful nil: +100 points
• Failed nil: -100 points
• Blind nil (bid before seeing cards): +/- 200 points

WINNING
First team to reach 500 points wins. If both teams reach 500 on the same hand, the team with the higher score wins.
    `
  },
  {
    id: "whist",
    name: "Whist",
    icon: Club,
    rules: `
WHIST - Official Rules

OVERVIEW
Whist is a classic trick-taking game for four players in partnerships. It's the predecessor to Bridge.

SETUP
• Use a standard 52-card deck
• Players sit across from their partners
• Deal 13 cards to each player, one at a time
• Last card dealt (to dealer) is turned face up to determine trump

CARD RANKING
• Ace (high) down to 2 (low)
• Trump suit beats all other suits

GAMEPLAY
• Player to dealer's left leads first trick
• Players must follow suit if possible
• If unable to follow suit, may play any card including trump
• Highest card of led suit wins unless trumped
• Highest trump wins if trumps are played
• Winner of trick leads next

SCORING
• Each trick over 6 = 1 point
• First team to 5 points wins the game
• Rubber: Best of 3 games

VARIATIONS
• Bid Whist: Players bid on number of tricks
• Solo Whist: Individual play, no partnerships
• Knock-Out Whist: Elimination style

STRATEGY TIPS
• Lead from your longest suit
• Return partner's led suit
• Save trump for critical moments
    `
  },
  {
    id: "hearts",
    name: "Hearts",
    icon: Heart,
    rules: `
HEARTS - Official Rules

OVERVIEW
Hearts is a trick-avoidance game for 3-6 players (best with 4). The goal is to have the lowest score.

SETUP
• Use a standard 52-card deck
• For 4 players: Deal 13 cards each
• Remove 2♦ for 3 players, add 2♦2♣ for 5-6 players

PASSING
• Before play begins, select 3 cards to pass
• Rotation: Left, Right, Across, No Pass (repeat)

GAMEPLAY
• Player with 2♣ leads first trick
• Must follow suit if possible
• Hearts cannot be led until "broken" (played on another suit)
• No point cards on first trick

SCORING
• Each Heart = 1 point
• Queen of Spades = 13 points
• "Shooting the Moon": Taking all hearts + Q♠ = 0 points for you, 26 for everyone else

WINNING
Game ends when someone reaches 100 points. Player with lowest score wins.
    `
  },
  {
    id: "bridge",
    name: "Bridge",
    icon: Diamond,
    rules: `
CONTRACT BRIDGE - Official Rules

OVERVIEW
Bridge is a partnership trick-taking game for four players. It consists of bidding and play phases.

SETUP
• Use a standard 52-card deck
• Partners sit across from each other
• Deal 13 cards to each player

BIDDING
• Clockwise from dealer
• Bids indicate tricks over 6 + trump suit or no-trump
• Example: "2 Hearts" = contract to win 8 tricks with hearts as trump
• Pass, Double, and Redouble are also options
• Three consecutive passes end bidding

PLAY
• Declarer (bid winner's partner) plays alone
• Dummy (declarer's partner) lays cards face up
• Declarer plays both hands
• Opening lead is to declarer's left

SCORING (SIMPLIFIED)
• Minor suits (♣♦): 20 points per trick
• Major suits (♥♠): 30 points per trick
• No-Trump: 40 first trick, 30 thereafter
• Game = 100 points below the line
• Bonuses for slam, rubber, honors

WINNING
First side to win two games wins the rubber.
    `
  },
  {
    id: "pinochle",
    name: "Pinochle",
    icon: Club,
    rules: `
PINOCHLE - Official Rules

OVERVIEW
Pinochle is a melding and trick-taking game using a 48-card deck (two of each 9-A in all suits).

SETUP
• Use Pinochle deck: A, 10, K, Q, J, 9 (×2 each suit)
• 4 players in partnerships
• Deal 12 cards each

BIDDING
• Start at 250, increase by 10s
• Bid represents total points team will score
• Highest bidder names trump

MELDING (Declare before play)
• Pinochle (J♦ + Q♠): 40 points
• Double Pinochle: 300 points
• Royal Marriage (K-Q of trump): 40 points
• Common Marriage (K-Q other suit): 20 points
• Run (A-10-K-Q-J of trump): 150 points
• Aces Around (all 4 aces): 100 points
• Kings/Queens/Jacks Around: 80/60/40 points

PLAY
• Standard trick-taking rules
• Must follow suit and beat if possible
• Aces, 10s, Kings = 10 points each
• Last trick = 10 points

WINNING
First team to 1500 points wins.
    `
  },
  {
    id: "euchre",
    name: "Euchre",
    icon: Spade,
    rules: `
EUCHRE - Official Rules

OVERVIEW
Euchre is a fast-paced trick-taking game for 4 players in partnerships using 24 cards.

SETUP
• Use 9, 10, J, Q, K, A of each suit (24 cards)
• Deal 5 cards to each player
• Turn up one card to propose trump

TRUMP ORDER (Unique to Euchre)
• Right Bower: Jack of trump (highest)
• Left Bower: Jack of same color as trump
• A, K, Q, 10, 9 of trump follow

MAKING TRUMP
• Players may "order up" the turned card or pass
• Dealer may "take it up" or turn it down
• If all pass, second round: players name any other suit
• "Going alone": Play without partner for bonus

GAMEPLAY
• 5 tricks per hand
• Must follow suit if possible
• Highest trump or led suit wins

SCORING
• Making team wins 3-4 tricks: 1 point
• Making team wins all 5 (march): 2 points
• Lone hand march: 4 points
• Euchred (makers don't make): Defenders get 2 points

WINNING
First team to 10 points wins.
    `
  },
  {
    id: "rummy",
    name: "Gin Rummy",
    icon: Diamond,
    rules: `
GIN RUMMY - Official Rules

OVERVIEW
Gin Rummy is a two-player card game focused on forming sets and runs.

SETUP
• Use standard 52-card deck
• Deal 10 cards to each player
• Place remaining cards face down (stock)
• Turn top card face up to start discard pile

CARD VALUES
• Face cards (K, Q, J): 10 points
• Aces: 1 point
• Number cards: Face value

MELDS
• Set: 3+ cards of same rank (e.g., 7-7-7)
• Run: 3+ consecutive cards of same suit (e.g., 4-5-6♠)

GAMEPLAY
• Draw from stock or discard pile
• Discard one card to end turn
• Arrange hand into melds
• Unmelded cards are "deadwood"

GOING OUT
• Gin: All cards melded, no deadwood - Bonus 25 points
• Knock: Deadwood ≤ 10 points
• Opponent may "lay off" cards on knocker's melds
• Undercut: If opponent has less deadwood, they score difference + 25

WINNING
First to 100 points wins the game.
    `
  },
  {
    id: "canasta",
    name: "Canasta",
    icon: Heart,
    rules: `
CANASTA - Official Rules

OVERVIEW
Canasta is a rummy-type game for 2-6 players focusing on creating melds of 7 cards.

SETUP
• Use two standard decks + 4 jokers (108 cards)
• 4 players in partnerships
• Deal 11 cards each
• Place stock, turn one card for discard pile

MELDS
• Minimum 3 cards of same rank
• Canasta: 7 cards of same rank
• Natural Canasta: No wild cards (500 points)
• Mixed Canasta: 1-3 wild cards (300 points)

WILD CARDS
• Jokers and 2s are wild
• Maximum 3 wild cards per meld
• Cannot meld all wild cards

SPECIAL CARDS
• Red 3s: Bonus 100 points each (800 for all 4)
• Black 3s: Block discard pile
• 7s and Aces: Must complete canasta to go out

GOING OUT
• Must have at least one canasta
• Meld or discard all remaining cards
• Bonus 100 points for going out

WINNING
First team to 5000 points wins.
    `
  },
  {
    id: "poker",
    name: "Texas Hold'em",
    icon: Spade,
    rules: `
TEXAS HOLD'EM POKER - Official Rules

OVERVIEW
Texas Hold'em is the world's most popular poker variant for 2-10 players.

SETUP
• Standard 52-card deck
• Dealer button rotates clockwise
• Small blind (left of dealer) and big blind post forced bets
• Deal 2 hole cards to each player

BETTING ROUNDS
1. Pre-Flop: After receiving hole cards
2. Flop: After 3 community cards dealt
3. Turn: After 4th community card
4. River: After 5th community card

ACTIONS
• Fold: Surrender hand
• Check: Pass action (if no bet)
• Call: Match current bet
• Raise: Increase the bet
• All-In: Bet all remaining chips

HAND RANKINGS (High to Low)
1. Royal Flush: A-K-Q-J-10 same suit
2. Straight Flush: 5 consecutive same suit
3. Four of a Kind: 4 same rank
4. Full House: 3 of a kind + pair
5. Flush: 5 same suit
6. Straight: 5 consecutive
7. Three of a Kind
8. Two Pair
9. One Pair
10. High Card

WINNING
Best 5-card hand using any combination of hole cards and community cards wins the pot.
    `
  },
  {
    id: "blackjack",
    name: "Blackjack",
    icon: Club,
    rules: `
BLACKJACK (21) - Official Rules

OVERVIEW
Blackjack is a casino banking game where players compete against the dealer.

OBJECTIVE
Get a hand value closer to 21 than the dealer without exceeding 21 (busting).

CARD VALUES
• Number cards: Face value
• Face cards (J, Q, K): 10
• Ace: 1 or 11 (player's choice)

GAMEPLAY
1. Players place bets
2. Dealer deals 2 cards to each player and self (one face up)
3. Players act on their hands
4. Dealer reveals and plays

PLAYER OPTIONS
• Hit: Take another card
• Stand: Keep current hand
• Double Down: Double bet, take exactly one more card
• Split: If pair, create two hands (additional bet required)
• Surrender: Forfeit half bet, fold hand

BLACKJACK
Natural 21 (Ace + 10-value card) pays 3:2

DEALER RULES
• Must hit on 16 or less
• Must stand on 17 or more
• Hits soft 17 (varies by casino)

WINNING
• Beat dealer's hand: Win 1:1
• Dealer busts: Win 1:1
• Blackjack: Win 3:2
• Push (tie): Bet returned
    `
  }
];

const BOARD_GAMES = [
  {
    id: "backgammon",
    name: "Backgammon",
    icon: Grid3X3,
    rules: `
BACKGAMMON - Official Rules

OVERVIEW
Backgammon is one of the oldest known board games. Two players move their 15 checkers around the board to bear them off first.

SETUP
• 24 triangular points in 4 quadrants
• Each player has 15 checkers
• Starting position: 2 on 24-point, 5 on 13-point, 3 on 8-point, 5 on 6-point
• Players move in opposite directions

DICE & MOVEMENT
• Roll two dice to determine moves
• Move checkers forward by dice values
• Can move one checker for both dice or two checkers
• Doubles: Play the number four times

HITTING & RE-ENTERING
• Landing on opponent's single checker (blot) sends it to the bar
• Checkers on bar must re-enter before other moves
• Re-enter on opponent's home board matching dice roll

BEARING OFF
• All 15 checkers must be in home board
• Roll exact number or higher to bear off
• Exact: Remove checker from that point
• Higher: Remove from highest occupied point

DOUBLING CUBE
• Starts at 1, can be doubled to 2, 4, 8, etc.
• Offer double before rolling
• Opponent can accept or forfeit current stakes

WINNING
• Single game: 1 point (or cube value)
• Gammon: 2× (opponent hasn't borne off any)
• Backgammon: 3× (opponent has checker on bar or in winner's home)
    `
  },
  {
    id: "checkers",
    name: "Checkers (Draughts)",
    icon: CircleDot,
    rules: `
CHECKERS (AMERICAN DRAUGHTS) - Official Rules

OVERVIEW
Checkers is a strategy board game for two players on an 8×8 board with 64 squares.

SETUP
• 8×8 board with alternating colors
• Each player has 12 pieces
• Place pieces on dark squares of first 3 rows
• Dark squares only are used for play

MOVEMENT
• Pieces move diagonally forward one square
• Must capture if possible (forced capture)
• Can only move to empty squares
• Multiple captures in one turn if available

CAPTURING
• Jump diagonally over opponent's piece to empty square beyond
• Captured piece is removed from board
• Chain captures: Continue jumping if possible
• Must complete all available jumps

KINGS
• Reach opponent's back row to become king
• Kings can move and capture both forward and backward
• Indicated by stacking two pieces

WINNING CONDITIONS
• Capture all opponent's pieces
• Block all opponent's pieces (no legal moves)
• Opponent resigns

DRAW CONDITIONS
• Agreement between players
• Same position repeated 3 times
• 40 moves without capture or king creation

INTERNATIONAL DRAUGHTS
• 10×10 board, 20 pieces each
• Kings can move multiple squares diagonally
• Must capture maximum number of pieces
    `
  }
];

const RulesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState("spades");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="rules-page">
      {/* Shared Header */}
      <SharedHeader 
        activeSection={null}
        setActiveSection={() => {}}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Main Content */}
      <main className="pt-16 max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="mb-8">
          <h2 className="font-bebas text-4xl md:text-5xl tracking-wider text-white">
            OFFICIAL GAME RULES
          </h2>
          <p className="mt-4 text-[#A1A1AA] font-ubuntu text-lg">
            Master the rules of your favorite card and board games. Select a game below to view its complete ruleset.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Card Games Section */}
          <div className="mb-8">
            <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-4">CARD GAMES</h3>
            <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto">
              {CARD_GAMES.map((game) => (
                <TabsTrigger
                  key={game.id}
                  value={game.id}
                  className="bg-[#0A0A0A] border border-white/10 data-[state=active]:bg-[#7a6200] data-[state=active]:border-[#7a6200] px-4 py-2"
                  data-testid={`tab-${game.id}`}
                >
                  <game.icon className="w-4 h-4 mr-2" />
                  {game.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Board Games Section */}
          <div className="mb-8">
            <h3 className="font-bebas text-2xl tracking-wider text-[#7a6200] mb-4">BOARD GAMES</h3>
            <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto">
              {BOARD_GAMES.map((game) => (
                <TabsTrigger
                  key={game.id}
                  value={game.id}
                  className="bg-[#0A0A0A] border border-white/10 data-[state=active]:bg-[#7a6200] data-[state=active]:border-[#7a6200] px-4 py-2"
                  data-testid={`tab-${game.id}`}
                >
                  <game.icon className="w-4 h-4 mr-2" />
                  {game.name}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Content */}
          {[...CARD_GAMES, ...BOARD_GAMES].map((game) => (
            <TabsContent key={game.id} value={game.id}>
              <Card className="bg-[#0A0A0A] border border-white/10">
                <CardHeader className="border-b border-white/10">
                  <CardTitle className="font-bebas text-3xl tracking-wider text-white flex items-center gap-3">
                    <game.icon className="w-8 h-8 text-[#7a6200]" />
                    {game.name.toUpperCase()}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <ScrollArea className="h-[500px]">
                    <pre className="text-[#A1A1AA] font-ubuntu text-sm whitespace-pre-wrap leading-relaxed">
                      {game.rules.trim()}
                    </pre>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#0A0A0A] py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[#52525B] font-ubuntu text-sm">
            © 2026 All Rights Reserved | Board2Boston™ | 713 Consulting and Development
          </p>
          <p className="text-[#52525B] font-ubuntu text-xs mt-2">
            1942 W. Gray St., Box #1259 | Houston, TX 77019 | United States
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RulesPage;
